import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { Newspaper, Clock, AlertCircle, TrendingUp, Building2, TrendingDown, Flame, ArrowUpRight, ArrowDownRight, BarChart3, Loader2 } from 'lucide-react'

const API_BASE = import.meta.env.VITE_API_URL || 'https://www.grad-job.com/api'

const TYPE_CONFIG = {
  招聘: { icon: Building2, color: 'bg-blue-100 text-blue-700', borderColor: 'border-blue-200' },
  截止提醒: { icon: AlertCircle, color: 'bg-red-100 text-red-700', borderColor: 'border-red-200' },
  经验: { icon: TrendingUp, color: 'bg-green-100 text-green-700', borderColor: 'border-green-200' },
  趋势: { icon: Newspaper, color: 'bg-purple-100 text-purple-700', borderColor: 'border-purple-200' }
}

// ========== 日度折线图组件 ==========
const DailyLineChart = ({ data, color, height = 120, showValues = true }) => {
  const width = 600
  const padding = { top: 25, right: 15, bottom: 25, left: 15 }
  const chartW = width - padding.left - padding.right
  const chartH = height - padding.top - padding.bottom

  const values = data.map(d => d.count || d)
  const maxVal = Math.max(...values)
  const minVal = Math.min(...values)
  const range = maxVal - minVal || 1
  const labelInterval = 2

  const points = values.map((v, i) => ({
    x: padding.left + (i / (values.length - 1)) * chartW,
    y: padding.top + chartH - ((v - minVal) / range) * chartH,
    count: v,
    label: data[i].date || '',
    showLabel: i % labelInterval === 0 || i === values.length - 1
  }))

  const linePath = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ')
  const areaPath = `${linePath} L ${points[points.length - 1].x} ${padding.top + chartH} L ${points[0].x} ${padding.top + chartH} Z`
  const gradientId = `grad-${color.replace('#', '')}-${Math.random().toString(36).substr(2, 5)}`

  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-full" preserveAspectRatio="none">
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.2" />
          <stop offset="100%" stopColor={color} stopOpacity="0.02" />
        </linearGradient>
      </defs>
      {[0.33, 0.66].map((ratio, i) => (
        <line key={`grid-${i}`} x1={padding.left} y1={padding.top + chartH * ratio}
          x2={width - padding.right} y2={padding.top + chartH * ratio} stroke="#f3f4f6" strokeWidth="1" />
      ))}
      <path d={areaPath} fill={`url(#${gradientId})`} />
      <path d={linePath} fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
      {points.map((p, i) => (
        <g key={`point-${i}`}>
          {i === points.length - 1 ? (
            <>
              <circle cx={p.x} cy={p.y} r="6" fill={color} opacity="0.2" />
              <circle cx={p.x} cy={p.y} r="4" fill="white" stroke={color} strokeWidth="2.5" />
            </>
          ) : (
            <circle cx={p.x} cy={p.y} r="3" fill="white" stroke={color} strokeWidth="1.5" />
          )}
          {showValues && (i === 0 || i === Math.floor(points.length / 2) || i === points.length - 1) && (
            <text x={p.x} y={p.y - 10} textAnchor="middle" className="text-xs font-bold" fill={color}>
              {p.count.toLocaleString()}
            </text>
          )}
        </g>
      ))}
      {points.filter(p => p.showLabel).map((p, i) => (
        <text key={`label-${i}`} x={p.x} y={height - 5} textAnchor="middle" className="text-xs font-medium" fill="#9ca3af">
          {p.label}
        </text>
      ))}
    </svg>
  )
}

// ========== 迷你日度图 ==========
const MiniDailyChart = ({ data, color, height = 28 }) => {
  const width = 80
  const padding = { top: 2, right: 2, bottom: 2, left: 2 }
  const chartW = width - padding.left - padding.right
  const chartH = height - padding.top - padding.bottom
  const values = data.map(d => d.count || d)
  const maxVal = Math.max(...values)
  const minVal = Math.min(...values)

  const points = values.map((v, i) => ({
    x: padding.left + (i / (values.length - 1)) * chartW,
    y: padding.top + chartH - ((v - minVal) / (maxVal - minVal)) * chartH
  }))

  const linePath = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ')

  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-full">
      <path d={linePath} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx={points[points.length - 1].x} cy={points[points.length - 1].y} r="3" fill={color} stroke="white" strokeWidth="1.5" />
    </svg>
  )
}

// ========== 总趋势面板 ==========
const TotalTrendLayer = ({ data }) => {
  if (!data || !data.daily || data.daily.length === 0) return null

  const daily = data.daily
  const current = daily[daily.length - 1].count
  const prev = daily[0].count
  const growth = ((current - prev) / prev * 100).toFixed(1)
  const yesterday = daily[daily.length - 2].count
  const dailyGrowth = current - yesterday
  const startDate = daily[0].date
  const endDate = daily[daily.length - 1].date

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-xl border border-gray-200 p-5">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center">
            <BarChart3 size={20} className="text-blue-600" />
          </div>
          <div>
            <h3 className="text-base font-bold text-gray-900">央企在招岗位总量</h3>
            <p className="text-xs text-gray-400">日度统计：{startDate} - {endDate}（共{daily.length}天）</p>
          </div>
        </div>
        <div className="flex items-center space-x-4">
          <div className="text-right">
            <p className="text-3xl font-bold text-gray-900">{current.toLocaleString()}</p>
          </div>
          <div className="flex flex-col items-end space-y-1">
            <span className="bg-green-50 text-green-700 px-2.5 py-1 rounded-lg text-sm font-bold flex items-center">
              <ArrowUpRight size={14} className="mr-1" />+{growth}%
            </span>
            <span className="text-xs text-gray-400">较{daily.length}天前 +{(current - prev).toLocaleString()}</span>
          </div>
        </div>
      </div>
      <div className="h-36">
        <DailyLineChart data={daily} color="#3b82f6" height={144} />
      </div>
      <div className="grid grid-cols-4 gap-3 mt-3">
        <div className="text-center p-2 bg-gray-50 rounded-lg">
          <p className="text-xs text-gray-500">{daily.length}天前</p>
          <p className="text-sm font-bold text-gray-700">{prev.toLocaleString()}</p>
        </div>
        <div className="text-center p-2 bg-gray-50 rounded-lg">
          <p className="text-xs text-gray-500">{Math.floor(daily.length / 2)}天前</p>
          <p className="text-sm font-bold text-gray-700">{daily[Math.floor(daily.length / 2)].count.toLocaleString()}</p>
        </div>
        <div className="text-center p-2 bg-gray-50 rounded-lg">
          <p className="text-xs text-gray-500">昨日</p>
          <p className="text-sm font-bold text-gray-700">{yesterday.toLocaleString()}</p>
        </div>
        <div className="text-center p-2 bg-blue-50 rounded-lg border border-blue-200">
          <p className="text-xs text-blue-600">今日</p>
          <p className="text-sm font-bold text-blue-700">{current.toLocaleString()}</p>
          <p className="text-[10px] text-green-600">+{dailyGrowth}</p>
        </div>
      </div>
    </motion.div>
  )
}

// ========== 岗位排行项 ==========
const JobRankRow = ({ job, index, type, startDate }) => {
  const isHot = type === 'hot'
  const color = isHot ? '#ef4444' : '#22c55e'
  const dailyData = job.daily.map((count, i) => ({ count, date: '' }))

  return (
    <motion.div initial={{ opacity: 0, x: isHot ? 10 : -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: index * 0.04 }}
      className="flex items-center justify-between py-3 px-3 hover:bg-gray-50 rounded-lg transition-colors">
      <div className="flex items-center space-x-3 flex-1 min-w-0">
        <span className={`text-xs font-bold w-6 h-6 flex items-center justify-center rounded-lg flex-shrink-0 ${
          index < 3 ? (isHot ? 'bg-red-100 text-red-600' : 'bg-orange-100 text-orange-600') : 'bg-gray-100 text-gray-500'
        }`}>{index + 1}</span>
        <div className="min-w-0">
          <p className="text-sm font-medium text-gray-800 truncate">{job.name}</p>
          <div className="flex items-center space-x-2 text-xs text-gray-400">
            <span>{startDate}: {job.daily[0]}</span>
            <span className="text-gray-300">→</span>
            <span className="font-medium text-gray-600">最新: {job.current}</span>
          </div>
        </div>
      </div>
      <div className="flex items-center space-x-3 flex-shrink-0">
        <div className="w-16 h-8"><MiniDailyChart data={dailyData} color={color} /></div>
        <span className={`text-sm font-bold w-14 text-right ${isHot ? 'text-red-500' : 'text-green-600'}`}>
          {isHot ? job.growth : job.decline}
        </span>
      </div>
    </motion.div>
  )
}

// ========== 火热岗位面板 ==========
const HotJobsPanel = ({ jobs, startDate }) => (
  <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }} className="bg-white rounded-xl border border-gray-200 p-4">
    <div className="flex items-center justify-between mb-3">
      <div className="flex items-center space-x-2">
        <div className="w-8 h-8 bg-red-50 rounded-lg flex items-center justify-center">
          <Flame size={16} className="text-red-500" />
        </div>
        <div>
          <span className="text-sm font-bold text-gray-900">火热岗位TOP5</span>
          <span className="text-xs text-gray-400 ml-2">14天涨幅</span>
        </div>
      </div>
      <span className="text-xs text-red-500 bg-red-50 px-2 py-1 rounded-full font-medium">需求量暴涨</span>
    </div>
    <div className="divide-y divide-gray-50">
      {jobs.map((job, index) => (
        <JobRankRow key={job.name} job={job} index={index} type="hot" startDate={startDate} />
      ))}
    </div>
  </motion.div>
)

// ========== 下降岗位面板 ==========
const DeclineJobsPanel = ({ jobs, startDate }) => (
  <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bg-white rounded-xl border border-gray-200 p-4">
    <div className="flex items-center justify-between mb-3">
      <div className="flex items-center space-x-2">
        <div className="w-8 h-8 bg-green-50 rounded-lg flex items-center justify-center">
          <TrendingDown size={16} className="text-green-500" />
        </div>
        <div>
          <span className="text-sm font-bold text-gray-900">下降岗位TOP5</span>
          <span className="text-xs text-gray-400 ml-2">14天跌幅</span>
        </div>
      </div>
      <span className="text-xs text-green-600 bg-green-50 px-2 py-1 rounded-full font-medium">需求量萎缩</span>
    </div>
    <div className="divide-y divide-gray-50">
      {jobs.map((job, index) => (
        <JobRankRow key={job.name} job={job} index={index} type="decline" startDate={startDate} />
      ))}
    </div>
  </motion.div>
)

// ========== 情报卡片 ==========
const IntelligenceCard = ({ item, index }) => {
  const config = TYPE_CONFIG[item.type] || TYPE_CONFIG['趋势']
  const Icon = config.icon

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.05 }}
      className={`card p-5 border-l-4 ${config.borderColor} hover:shadow-md transition-shadow`}>
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center space-x-2">
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.color} flex items-center`}>
            <Icon size={12} className="mr-1" />{item.type}
          </span>
          {item.isUrgent && (
            <span className="px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs font-bold">紧急</span>
          )}
        </div>
        <span className="text-xs text-gray-400">{item.publishTime}</span>
      </div>
      <h3 className="font-bold text-gray-900 mb-2">{item.title}</h3>
      <p className="text-sm text-gray-600 mb-3">{item.summary}</p>
      <div className="flex items-center justify-between">
        <span className="text-xs text-gray-400">来源: {item.source}</span>
        <div className="flex space-x-2">
          {item.tags && item.tags.map(tag => (
            <span key={tag} className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs">{tag}</span>
          ))}
        </div>
      </div>
    </motion.div>
  )
}

// ========== 主页面 ==========
const IntelligencePage = () => {
  const [intelligence, setIntelligence] = useState([])
  const [trendData, setTrendData] = useState(null)
  const [filter, setFilter] = useState('all')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [lastUpdate, setLastUpdate] = useState('')

  // 获取趋势数据
  useEffect(() => {
    const fetchTrends = async () => {
      try {
        const res = await fetch(API_BASE + '/admin/trends/public')
        const json = await res.json()
        if (json.status === 200 && json.data) {
          setTrendData(json.data)
          if (json.data.totalJobs && json.data.totalJobs.updatedAt) {
            const d = new Date(json.data.totalJobs.updatedAt)
            setLastUpdate(d.toLocaleString('zh-CN', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }))
          }
        }
      } catch (err) {
        console.error('Failed to fetch trends:', err)
      }
    }
    fetchTrends()
  }, [])

  // 获取情报数据
  useEffect(() => {
    const fetchIntelligence = async () => {
      try {
        setLoading(true)
        const res = await fetch(API_BASE + '/admin/intelligence/public?page=1&limit=20')
        const json = await res.json()
        if (json.status === 200 && json.data && json.data.list) {
          setIntelligence(json.data.list)
        }
      } catch (err) {
        console.error('Failed to fetch intelligence:', err)
        setError('数据加载失败，请稍后重试')
      } finally {
        setLoading(false)
      }
    }
    fetchIntelligence()
  }, [])

  const filters = [
    { key: 'all', label: '全部' },
    { key: '招聘', label: '招聘' },
    { key: '截止提醒', label: '截止提醒' },
    { key: '经验', label: '经验分享' },
    { key: '趋势', label: '行业趋势' }
  ]

  const filteredData = filter === 'all'
    ? intelligence
    : intelligence.filter(item => item.type === filter)

  const startDate = trendData && trendData.totalJobs && trendData.totalJobs.daily && trendData.totalJobs.daily.length > 0
    ? trendData.totalJobs.daily[0].date
    : ''

  return (
    <div className="max-w-6xl mx-auto">
      {/* 页面标题 */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-6">
        <div className="inline-flex items-center px-4 py-2 bg-primary-100 text-primary-700 rounded-full text-sm font-medium mb-4">
          <Newspaper size={16} className="mr-2" />
          每日更新
        </div>
        <h1 className="section-title">央国企求职情报</h1>
        <p className="section-subtitle">最新招聘、截止提醒、经验分享、行业趋势</p>
      </motion.div>

      {/* 趋势图区域 */}
      {trendData && trendData.totalJobs && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="mb-6">
          <TotalTrendLayer data={trendData.totalJobs} />
          <div className="grid md:grid-cols-2 gap-4 mt-4">
            {trendData.hotJobs && <HotJobsPanel jobs={trendData.hotJobs} startDate={startDate} />}
            {trendData.declineJobs && <DeclineJobsPanel jobs={trendData.declineJobs} startDate={startDate} />}
          </div>
        </motion.div>
      )}

      {/* 筛选器 */}
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }} className="flex flex-wrap gap-2 mb-6">
        {filters.map(f => (
          <button key={f.key} onClick={() => setFilter(f.key)}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
              filter === f.key ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}>
            {f.label}
          </button>
        ))}
      </motion.div>

      {/* 加载状态 */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 size={32} className="animate-spin text-primary-500 mr-3" />
          <span className="text-gray-500">加载中...</span>
        </div>
      )}

      {/* 错误状态 */}
      {error && !loading && (
        <div className="text-center py-12">
          <AlertCircle size={48} className="mx-auto text-red-300 mb-4" />
          <p className="text-red-500">{error}</p>
        </div>
      )}

      {/* 情报列表 */}
      {!loading && !error && (
        <div className="space-y-4">
          {filteredData.map((item, index) => (
            <IntelligenceCard key={item.id} item={item} index={index} />
          ))}
        </div>
      )}

      {filteredData.length === 0 && !loading && (
        <div className="text-center py-12">
          <Newspaper size={48} className="mx-auto text-gray-300 mb-4" />
          <p className="text-gray-500">暂无该类情报</p>
        </div>
      )}

      {/* 更新时间 */}
      <div className="mt-8 text-center text-xs text-gray-400">
        <div className="flex items-center justify-center space-x-2">
          <Clock size={14} />
          <span>每日凌晨2:00-4:00自动更新{lastUpdate ? ' | 上次更新: ' + lastUpdate : ''}</span>
        </div>
      </div>
    </div>
  )
}

export default IntelligencePage
